<script>

import api from "@/api";
import Vue from "vue";

/**
 * @typedef {Object} CorpusItem
 * @property {number} corpus_id - ID корпуса
 * @property {string} corpus_name - Название корпуса
 * @property {number} total - Количество элементов
 * @property {string} discipline - Название дисциплины
 * @property {string} spec_name - Название специальности
 * @property {string} wf_code - Код рабочей функции
 * @property {string} wf_name - Название рабочей функции
 */

export default {
  name: "SpecialityChess",
  data() {
    return {
      loading: false,
      wfData: {},
      items: {},
      speciality: "-"
    };
  },
  mounted() {
    this.loadData();
  },
  computed: {
    wfList() {
      return Object.keys(this.wfData).sort((a, b) => a.localeCompare(b));
    },
    title() {
      return `[${this.$route.params.code}] ${this.speciality ?? ""}`;
    },
    sortedItems() {
      return Object.entries(this.items)
        .map(([discipline, disciplineInfo]) => ({ ...disciplineInfo, name: discipline }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
  },
  methods: {
    async loadData() {
      this.loading = true;
      return api.get(`/corpus/stats/speciality/${this.$route.params.code}/chess`).then((response) => {
        this.response = response.data;
        if (response.status === 200) {
          this.parseData(response.data);
        }
      }).finally(() => this.loading = false);
    },
    /**
     * Парсит массив данных в иерархическую структуру
     * @param {CorpusItem[]} data - Входные данные
     */
    parseData(data) {
      data.forEach((item) => {
        this.speciality = item.spec_name;
        if (!this.items[item.discipline]) {
          Vue.set(this.items, item.discipline, {
            total: 0,
            wf: {}
          });
        }
        if (!this.items[item.discipline].wf[item.wf_code]) {
          this.items[item.discipline].wf[item.wf_code] = {
            total: 0,
            corpus: {}
          };
        }

        if (!this.items[item.discipline].wf[item.wf_code].corpus[item.corpus_id]) {
          this.items[item.discipline].wf[item.wf_code].corpus[item.corpus_id] = {
            name: item.corpus_name,
            total: item.total
          };
        }
        if (!this.wfData[item.wf_code]) {
          Vue.set(this.wfData, item.wf_code, { name: item.wf_name, total: 0 });
        }
        this.items[item.discipline].total += item.total;
        this.items[item.discipline].wf[item.wf_code].total += item.total;
        this.wfData[item.wf_code].total += item.total;

      });
    },
    /**
     * Экспортирует HTML таблицу в Excel файл (XLS формат) без зависимостей
     * @param {string|HTMLElement} table - ID таблицы или DOM-элемент таблицы
     * @param {string} [filename='export.xls'] - Имя файла для сохранения
     */
    exportTableToExcelSimple(table, filename = "export") {
      const tableEl = typeof table === "string"
        ? document.getElementById(table)
        : table;

      if (!tableEl || tableEl.tagName !== "TABLE") {
        console.error("Invalid table element or ID");
        return;
      }

      // Кодируем HTML в base64 напрямую
      const html = tableEl.outerHTML;
      const uri = "data:application/vnd.ms-excel;base64,";

      // noinspection HtmlRequiredLangAttribute,XmlUnusedNamespaceDeclaration
      const template = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
                  xmlns:x="urn:schemas-microsoft-com:office:excel"
                  xmlns="http://www.w3.org/TR/REC-html40">
                  <head><!--[if gte mso 9]>
                  <xml>
                    <x:ExcelWorkbook>
                      <x:ExcelWorksheets>
                        <x:ExcelWorksheet>
                          <x:Name>{worksheet}</x:Name>
                          <x:WorksheetOptions>
                            <x:DisplayGridlines/>
                          </x:WorksheetOptions>
                        </x:ExcelWorksheet>
                      </x:ExcelWorksheets>
                    </x:ExcelWorkbook>
                  </xml>
                  <![endif]--></head>
                  <body>${html}</body></html>`;

      // Преобразуем в base64 без использования unescape
      const base64 = window.btoa(encodeURIComponent(template).replace(
        /%([0-9A-F]{2})/g,
        (match, p1) => String.fromCharCode(parseInt(p1, 16))
      ));

      // Создаём и триггерим скачивание
      const link = document.createElement("a");
      link.download = `${filename}.xls`;
      link.href = uri + base64;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};

</script>

<template>
  <b-card>
    <template #header>
      <h4>
        {{ title }}
        <b-btn @click="exportTableToExcelSimple('chess', speciality)">
          Скачать
        </b-btn>
      </h4>

    </template>
    <b-table-simple id="chess" hover bordered small>
      <colgroup>
        <col style="width: auto"> <!-- name -->
        <!--suppress JSUnusedLocalSymbols -->
        <col v-for="_ in wfList" :style="{ width: '80px' }">
        <col style="width: 80px"> <!-- name -->
      </colgroup>
      <b-thead>
        <b-tr>
          <b-th>Дисциплина</b-th>
          <b-th
            v-for="wf in wfList"
            :title="wfData[wf].name"
            v-b-tooltip.hover
          >
            {{ wf }}
          </b-th>
          <b-th>Итого</b-th>
        </b-tr>
      </b-thead>

      <b-tbody>
        <template v-if="loading">
          <tr>
            <td :colspan="wfList.length + 2">
              <div class="text-center text-danger my-2">
                <b-spinner class="align-middle mr-2"></b-spinner>
                <strong>Загрузка...</strong>
              </div>
            </td>
          </tr>
        </template>
        <template v-else-if="Object.values(items).length === 0">
          <tr>
            <td :colspan="wfList.length + 2">
              <div class="text-center text-danger my-2">
                <strong>Нет данных</strong>
              </div>
            </td>
          </tr>
        </template>
        <template v-else>

          <b-tr v-for="item in sortedItems">
            <b-th>
              {{ item.name }}
            </b-th>
            <b-td v-for="wf in wfList">
              <template v-if="item.wf[wf]">
                <!-- Chess Ячейка -->
                <div class="d-flex justify-content-between align-items-center">
                  {{ item.wf[wf].total }}
                  <b-btn size="sm" pill :id="`corpus-${item.name}-${wf}`" variant="light">
                    <font-awesome-icon icon="folder-open" />
                    <!-- [{{ Object.keys(disciplineInfo.wf[wf].corpus).length }}]-->
                  </b-btn>
                </div>

                <b-popover :target="`corpus-${item.name}-${wf}`" triggers="hover" placement="top">
                  <b-list-group>
                    <b-list-group-item v-for="(corpusInfo, corpusId) in item.wf[wf].corpus">
                      <b-link :to="{name: 'CorpusChunks', params: {corpusId}, query: {discipline: item.name, wf}}">
                        {{ corpusInfo.name }}
                        <b-badge>
                          {{ corpusInfo.total }}
                        </b-badge>
                      </b-link>
                    </b-list-group-item>
                  </b-list-group>
                </b-popover>
              </template>
            </b-td>
            <b-th>
              {{ item.total }}
            </b-th>
          </b-tr>
        </template>
      </b-tbody>

      <b-tfoot v-if="Object.values(items).length > 0">
        <b-tr>
          <th class="text-right">Итого</th>
          <th v-for="wf in wfList">
            {{ wfData[wf].total }}
          </th>
          <b-th>
            {{ Object.values(wfData).reduce((sum, item) => sum + item.total, 0) }}
          </b-th>
        </b-tr>
      </b-tfoot>

    </b-table-simple>

  </b-card>
</template>


<style scoped>
</style>